import React, { useEffect, useState } from 'react';
import { Box, Popover, Avatar, Typography } from '@mui/material';
import { useChatStore } from '@client/store';
import { Message } from '@shared-types';
import { styled } from '@mui/system';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

interface Node {
  id: string;
  type: 'user' | 'char';
  text: string;
  avatar?: string;
  children: Node[];
}

const Circle = styled(Box)({
  borderRadius: '50%',
  width: 70,
  height: 70,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  position: 'relative',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'scale(1.1)',
    boxShadow: '0 0 20px rgba(255,255,255,0.5)',
    backdropFilter: 'blur(6px)',
  },
  background: 'rgba(255,255,255,0.1)',
  border: '1px solid rgba(255,255,255,0.3)',
});

const Line = styled('div')({
  width: 4,
  borderRadius: 2,
  margin: '0 auto',
  background:
    'linear-gradient(180deg, rgba(255,255,255,0.4), rgba(255,255,255,0.1))',
  filter: 'drop-shadow(0 2px 6px rgba(255,255,255,0.2))',
});

function buildTree(messages: Message[]): Node[] {
  if (messages.length === 0) return [];

  const rootMsg = messages.find((m) => m.is_first);
  if (!rootMsg) return [];

  const root: Node = {
    id: `${rootMsg.type}-${rootMsg.id}`,
    type: rootMsg.type!,
    text: rootMsg.text || rootMsg.chunks?.join('') || '',
    avatar: undefined,
    children: [],
  };

  let lastNode: Node = root;

  messages
    .filter((m) => !m.is_first)
    .forEach((msg) => {
      const node: Node = {
        id: `${msg.type}-${msg.id}`,
        type: msg.type!,
        text: msg.text || msg.chunks?.join('') || '',
        avatar: undefined,
        children: [],
      };
      lastNode.children.push(node);
      lastNode = node;
    });

  return [root];
}

function TreeNode({ node }: { node: Node }) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const handleClick = (e: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(anchorEl ? null : e.currentTarget);
  const open = Boolean(anchorEl);

  return (
    <Box display="flex" flexDirection="column" alignItems="center" mb={6}>
      <Circle onClick={handleClick}>
        <Avatar
          src={node.avatar}
          sx={{
            width: 48,
            height: 48,
            border: '2px solid rgba(255,255,255,0.6)',
          }}
        />
      </Circle>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{
          sx: {
            p: 2,
            maxWidth: 250,
            borderRadius: 2,
            backdropFilter: 'blur(10px)',
            background: 'rgba(255,255,255,0.1)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
            color: '#fff',
          },
        }}
      >
        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
          {node.text}
        </Typography>
      </Popover>

      {node.children.length > 0 && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="flex-start"
          mt={3}
          width="100%"
        >
          {node.children.map((child) => (
            <Box
              key={child.id}
              display="flex"
              flexDirection="column"
              alignItems="center"
              mx={4}
            >
              <Line style={{ height: 40 }} />
              <TreeNode node={child} />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

export function ChatHistory() {
  const { messages } = useChatStore();
  const [treeData, setTreeData] = useState<Node[]>([]);

  useEffect(() => {
    if (messages.length > 0) setTreeData(buildTree(messages));
  }, [messages]);

  return (
    <TransformWrapper
      initialScale={1}
      minScale={0.5}
      maxScale={2}
      wheel={{ step: 0.1 }}
    >
      <TransformComponent>
        <Box
          width="100%"
          height="100%"
          display="flex"
          justifyContent="center"
          alignItems="flex-start"
          overflow="auto"
          p={4}
        >
          {treeData.length > 0 ? (
            treeData.map((node) => <TreeNode key={node.id} node={node} />)
          ) : (
            <Box
              sx={{
                p: 3,
                borderRadius: 2,
                backdropFilter: 'blur(10px)',
                background: 'rgba(255,255,255,0.05)',
                color: '#fff',
              }}
            >
              <Typography>No messages</Typography>
            </Box>
          )}
        </Box>
      </TransformComponent>
    </TransformWrapper>
  );
}
